import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useLang } from '../context/LangContext';
import { API_URL, resolveImage } from '../config';

const API = API_URL;

const ProductDetail = () => {
  const { id } = useParams();
  const { dispatch, items } = useCart();
  const { t } = useLang();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [modelSearch, setModelSearch] = useState('');
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSelectedVariant(null);
    setModelSearch('');
    setActiveImage(null);
    window.scrollTo(0, 0);
    axios.get(`${API}/products/${id}`)
      .then(r => {
        setProduct(r.data.data);
        if (r.data.data.variants?.length) {
          const firstVariant = r.data.data.variants[0];
          setSelectedVariant(firstVariant);
          setActiveImage(firstVariant.image || r.data.data.image);
        } else {
          setActiveImage(r.data.data.image);
        }
        return axios.get(`${API}/products?category=${r.data.data.category}&limit=4`);
      })
      .then(r => setRelated(r.data.data.filter(p => p.id !== parseInt(id))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  // Galerie style eBay : image de couverture + photos additionnelles (dédupliquées).
  const galleryImages = product
    ? [product.image, ...(product.images || []).map(i => i.url)].filter((url, i, arr) => url && arr.indexOf(url) === i)
    : [];

  const hasVariants = product?.variants?.length > 0;
  const filteredVariants = hasVariants
    ? product.variants.filter(v => v.model.toLowerCase().includes(modelSearch.toLowerCase()))
    : [];

  const effectivePrice = parseFloat(hasVariants ? (selectedVariant?.price ?? product?.price) : product?.price) || 0;
  const effectiveStock = hasVariants ? (selectedVariant?.stock ?? 0) : product?.stock;
  const cartItemId      = hasVariants && selectedVariant ? `${product.id}-v${selectedVariant.id}` : product?.id;

  const inCart   = items.find(i => i.id === cartItemId);
  const discount = product?.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;

  const handleAdd = () => {
    if (hasVariants && !selectedVariant) return;
    const payload = hasVariants
      ? {
          id: cartItemId,
          productId: product.id,
          variantId: selectedVariant.id,
          name: `${product.name} — ${selectedVariant.model}`,
          price: effectivePrice,
          originalPrice: product.originalPrice,
          image: selectedVariant.image || product.image,
          category: product.category,
          badge: product.badge,
          stock: selectedVariant.stock,
        }
      : { ...product, productId: product.id, variantId: null };
    for (let i = 0; i < qty; i++) dispatch({ type: 'ADD_ITEM', payload });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (loading) return (
    <div className="page-loader">
      <div className="spinner" />
    </div>
  );

  if (!product) return (
    <div className="not-found-page">
      <span>😕</span>
      <h2>{t('not_found')}</h2>
      <Link to="/" className="btn btn--primary">{t('back_home')}</Link>
    </div>
  );

  return (
    <div className="detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="breadcrumb__inner">
          <Link to="/">{t('nav_home')}</Link>
          <span>›</span>
          <Link to={`/category/${product.category}`}>{product.category}</Link>
          <span>›</span>
          <span>{product.name}</span>
        </div>
      </div>

      {/* Main product section */}
      <div className="detail-main">
        <div className="detail-image">
          <div className="detail-image__main">
            <img src={resolveImage(activeImage || product.image)} alt={product.name} />
            {product.badge && (
              <span className={`product-badge product-badge--${product.badge.toLowerCase().replace(' ', '-')}`}>
                {product.badge}
              </span>
            )}
            {discount && <span className="discount-tag">-{discount}%</span>}
          </div>

          {galleryImages.length > 1 && (
            <div className="detail-image__thumbs">
              {galleryImages.map((url, i) => (
                <button
                  type="button"
                  key={url + i}
                  className={`detail-image__thumb ${activeImage === url ? 'detail-image__thumb--active' : ''}`}
                  onClick={() => setActiveImage(url)}
                >
                  <img src={resolveImage(url)} alt={`${product.name} ${i + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="detail-info">
          <p className="product-card__category">{product.category}</p>
          <h1 className="detail-name">{product.name}</h1>

          <div className="product-card__rating" style={{ marginBottom: 20 }}>
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <span key={i} className={i < Math.floor(product.rating) ? 'star star--full' : 'star star--empty'}>★</span>
              ))}
            </div>
            <span className="rating-score">{product.rating}</span>
            <span className="rating-count">({product.reviews?.toLocaleString()} {t('reviews')})</span>
          </div>

          <div className="detail-price">
            <span className="detail-price__current">{Number(effectivePrice).toLocaleString()} TND</span>
            {product.originalPrice && (
              <>
                <span className="detail-price__original">{product.originalPrice.toLocaleString()} TND</span>
                <span className="detail-price__save">{t('you_save')} {(product.originalPrice - product.price).toLocaleString()} TND</span>
              </>
            )}
          </div>

          <p className="detail-desc">{product.description}</p>

          {hasVariants && (
            <div className="model-selector">
              <label className="model-selector__label">
                {t('select_model')} <span className="model-selector__count">({product.variants.length} {t('models_available')})</span>
              </label>
              <input
                type="text"
                className="model-selector__search"
                placeholder={t('select_model_placeholder')}
                value={modelSearch}
                onChange={e => setModelSearch(e.target.value)}
              />
              <div className="model-selector__list">
                {filteredVariants.map(v => (
                  <button
                    type="button"
                    key={v.id}
                    className={`model-chip ${v.image ? 'model-chip--with-image' : ''} ${selectedVariant?.id === v.id ? 'model-chip--active' : ''} ${v.stock === 0 ? 'model-chip--out' : ''}`}
                    onClick={() => {
                      setSelectedVariant(v);
                      setActiveImage(v.image || product.image);
                    }}
                  >
                    {v.image && (
                      <img src={resolveImage(v.image)} alt={v.model} className="model-chip__thumb" />
                    )}
                    {v.model}
                    {v.stock === 0 && <span className="model-chip__out"> · {t('out_of_stock')}</span>}
                  </button>
                ))}
                {filteredVariants.length === 0 && (
                  <p className="model-selector__empty">{t('no_results')}</p>
                )}
              </div>
            </div>
          )}

          <div className="detail-stock">
            <span className={`stock-badge ${effectiveStock > 10 ? 'stock-badge--ok' : effectiveStock > 0 ? 'stock-badge--low' : 'stock-badge--out'}`}>
              {effectiveStock > 10 ? `✓ ${t('in_stock')}` : effectiveStock > 0 ? `⚠ ${t('low_stock')} (${effectiveStock})` : `✕ ${t('out_of_stock')}`}
            </span>
          </div>

          <div className="detail-actions">
            <div className="detail-qty">
              <button className="qty-btn" onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
              <span className="qty-value">{qty}</span>
              <button className="qty-btn" onClick={() => setQty(q => Math.min(effectiveStock, q + 1))}>+</button>
            </div>
            <button
              className={`add-to-cart-btn add-to-cart-btn--lg ${added ? 'add-to-cart-btn--added' : ''}`}
              onClick={handleAdd}
              disabled={effectiveStock === 0 || (hasVariants && !selectedVariant)}
            >
              {added
                ? `✓ ${t('added')}`
                : inCart
                  ? `${t('in_cart')} (${inCart.quantity}) — ${t('add_more')}`
                  : `🛒 ${t('add_to_cart')} — ${(effectivePrice * qty).toLocaleString()} TND`}
            </button>
          </div>

          <div className="detail-badges">
            <div className="detail-badge"><span>🚚</span><div><strong>{t('free_delivery')}</strong><small>{t('free_delivery_sub')}</small></div></div>
            <div className="detail-badge"><span>🔄</span><div><strong>{t('easy_return')}</strong><small>{t('easy_return_sub')}</small></div></div>
            <div className="detail-badge"><span>🛡</span><div><strong>{t('warranty')}</strong><small>{t('warranty_sub')}</small></div></div>
          </div>
        </div>
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <div className="detail-related">
          <h2 className="section-title">{t('related_products')}</h2>
          <div className="product-grid">
            {related.slice(0, 3).map(p => (
              <Link to={`/product/${p.id}`} key={p.id} className="related-card">
                <img src={resolveImage(p.image)} alt={p.name} />
                <div className="related-card__info">
                  <p>{p.name}</p>
                  <strong>{p.price.toLocaleString()} TND</strong>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
